import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { enAU } from 'date-fns/locale';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BookingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [offDays, setOffDays] = useState([]);
  const [settings, setSettings] = useState({ closedDates: [] });
  
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null); // {duration, price}
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [clientData, setClientData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [servicesRes, professionalsRes, bookingsRes, blockedRes, offDaysRes, settingsRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/professionals`),
        axios.get(`${API}/bookings`),
        axios.get(`${API}/blocked-slots`),
        axios.get(`${API}/off-days`),
        axios.get(`${API}/settings`)
      ]);
      setServices(servicesRes.data);
      setProfessionals(professionalsRes.data);
      setBookings(bookingsRes.data);
      setBlockedSlots(blockedRes.data);
      setOffDays(offDaysRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  // Format price in AUD
  const formatPrice = (price) => {
    return price.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
  };

  const filteredProfessionals = selectedService
    ? professionals.filter(p => p.allowedServices.includes(selectedService.name))
    : [];

  const generateTimeSlots = () => {
    if (!selectedDate || !selectedProfessional) return [];
    
    const slots = [];
    const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][selectedDate.getDay()];
    const professionalHours = selectedProfessional.workingHours?.[dayName];
    
    if (!professionalHours || !professionalHours.enabled) {
      return [];
    }
    
    const [startHour, startMin] = professionalHours.start.split(':').map(Number);
    const [endHour, endMin] = professionalHours.end.split(':').map(Number);
    
    let currentHour = startHour;
    let currentMin = startMin;
    
    while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
      const timeStr = `${currentHour.toString().padStart(2, '0')}:${currentMin.toString().padStart(2, '0')}`;
      slots.push(timeStr);
      
      currentMin += 30;
      if (currentMin >= 60) {
        currentMin = 0;
        currentHour += 1;
      }
    }
    
    return slots;
  };

  const isSlotAvailable = (time) => {
    if (!selectedDate || !selectedProfessional) return false;
    
    // Check if time slot is in the past
    const now = new Date();
    const slotDate = new Date(selectedDate);
    const [hours, minutes] = time.split(':').map(Number);
    slotDate.setHours(hours, minutes, 0, 0);
    
    if (slotDate < now) {
      return false; // Slot is in the past
    }
    
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    // Check both English and Spanish status values for compatibility
    const isBooked = bookings.some(
      b => b.date === dateStr && 
           b.time === time && 
           b.professionalName === selectedProfessional.name &&
           (b.status === 'pending' || b.status === 'approved' || 
            b.status === 'pendiente' || b.status === 'aprobado')
    );
    
    const isBlocked = blockedSlots.some(
      s => s.date === dateStr && 
           s.time === time && 
           s.professionalName === selectedProfessional.name
    );
    
    return !isBooked && !isBlocked;
  };

  // Get the actual price and duration (from selected duration option or base service)
  const getSelectedPrice = () => {
    if (selectedDuration) return selectedDuration.price;
    return selectedService?.price || 0;
  };
  
  const getSelectedDuration = () => {
    if (selectedDuration) return selectedDuration.duration;
    return selectedService?.duration || 0;
  };

  const handleConfirmBooking = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const finalPrice = getSelectedPrice();
      const finalDuration = getSelectedDuration();
      
      // Create booking first
      const bookingResponse = await axios.post(`${API}/bookings`, {
        serviceName: selectedService.name,
        professionalName: selectedProfessional.name,
        date: dateStr,
        time: selectedTime,
        clientName: clientData.name,
        clientEmail: clientData.email,
        clientPhone: clientData.phone,
        duration: finalDuration,
        price: finalPrice
      });
      
      const bookingId = bookingResponse.data.id;
      
      // Create Stripe checkout session
      const paymentResponse = await axios.post(`${API}/payments/create-checkout`, {
        booking_id: bookingId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      if (paymentResponse.data.url) {
        window.location.href = paymentResponse.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      toast.error('Booking error', {
        description: error.response?.data?.detail || 'Please try again'
      });
    }
  };

  const disabledDays = [
    { dayOfWeek: [0] }, // Sunday always disabled
    { before: today }, // Past dates
    // Center closed dates
    ...(settings.closedDates || []).map(date => new Date(date + 'T00:00:00')),
    // Selected professional's off days
    ...(selectedProfessional ? offDays
      .filter(od => od.professionalName === selectedProfessional.name)
      .map(od => new Date(od.date + 'T00:00:00'))
      : [])
  ];

  return (
    <div className="min-h-screen bg-[#faf8f5] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => step === 1 ? navigate('/') : setStep(step - 1)} data-testid="back-btn">
            <ArrowLeft className="mr-2" /> Back
          </Button>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold elegant-script mb-4" data-testid="booking-title">Book Appointment</h1>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((s) => (
              <div key={s} className={`h-2 flex-1 rounded-full ${s <= step ? 'bg-black' : 'bg-gray-300'}`} data-testid={`progress-step-${s}`} />
            ))}
          </div>
        </div>

        {step === 1 && (
          <div data-testid="step-1-service">
            <h2 className="text-2xl font-bold mb-6">1. Choose a Service</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {services.map((service) => {
                const hasDurations = service.durations && service.durations.length > 0;
                const minPrice = hasDurations 
                  ? Math.min(...service.durations.map(d => d.price))
                  : service.price;
                const maxPrice = hasDurations 
                  ? Math.max(...service.durations.map(d => d.price))
                  : service.price;
                
                return (
                  <Card 
                    key={service.id}
                    className={`cursor-pointer card-hover ${selectedService?.id === service.id ? 'ring-2 ring-black' : ''}`}
                    onClick={() => {
                      setSelectedService(service);
                      setSelectedDuration(null);
                      setSelectedProfessional(null);
                    }}
                    data-testid={`service-option-${service.id}`}
                  >
                    <CardContent className="p-4">
                      <h3 className="font-bold mb-2">{service.name}</h3>
                      <p className="text-sm text-gray-600 mb-2">{service.description}</p>
                      <div className="flex justify-between text-sm">
                        <span>
                          {hasDurations 
                            ? `${Math.min(...service.durations.map(d => d.duration))}-${Math.max(...service.durations.map(d => d.duration))} min`
                            : `${service.duration} min`
                          }
                        </span>
                        <span className="font-bold">
                          {minPrice === maxPrice 
                            ? formatPrice(minPrice)
                            : `${formatPrice(minPrice)} - ${formatPrice(maxPrice)}`
                          }
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            {/* Duration selection if service has multiple durations */}
            {selectedService && selectedService.durations && selectedService.durations.length > 0 && (
              <div className="mt-6">
                <Label className="mb-3 block font-semibold">Select Duration:</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {selectedService.durations.map((opt, idx) => (
                    <Card
                      key={idx}
                      className={`cursor-pointer card-hover text-center ${selectedDuration?.duration === opt.duration ? 'ring-2 ring-black bg-black text-white' : ''}`}
                      onClick={() => setSelectedDuration(opt)}
                      data-testid={`duration-option-${opt.duration}`}
                    >
                      <CardContent className="p-3">
                        <p className="font-bold">{opt.duration} min</p>
                        <p className={`text-sm ${selectedDuration?.duration === opt.duration ? 'text-gray-200' : 'text-gray-600'}`}>
                          {formatPrice(opt.price)}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
            
            <Button 
              className="mt-6 w-full bg-black text-[#e8dcc8]" 
              disabled={!selectedService || (selectedService.durations?.length > 0 && !selectedDuration)}
              onClick={() => setStep(2)}
              data-testid="next-to-professional-btn"
            >
              Continue
            </Button>
          </div>
        )}

        {step === 2 && (
          <div data-testid="step-2-professional">
            <h2 className="text-2xl font-bold mb-6">2. Choose a Professional</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {filteredProfessionals.map((prof) => (
                <Card 
                  key={prof.id}
                  className={`cursor-pointer card-hover text-center ${selectedProfessional?.id === prof.id ? 'ring-2 ring-black' : ''}`}
                  onClick={() => setSelectedProfessional(prof)}
                  data-testid={`professional-option-${prof.id}`}
                >
                  <CardContent className="p-4">
                    <img src={prof.imageUrl} alt={prof.name} className="w-24 h-24 rounded-full mx-auto mb-3 object-cover" />
                    <h3 className="font-bold mb-1">{prof.name}</h3>
                    <p className="text-sm text-gray-600">{prof.specialty}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <Button 
              className="mt-6 w-full bg-black text-[#e8dcc8]" 
              disabled={!selectedProfessional}
              onClick={() => setStep(3)}
              data-testid="next-to-datetime-btn"
            >
              Continue
            </Button>
          </div>
        )}

        {step === 3 && (
          <div data-testid="step-3-datetime">
            <h2 className="text-2xl font-bold mb-6">3. Choose Date & Time</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="mb-2 block">Select a date</Label>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={disabledDays}
                  locale={enAU}
                  className="rounded-md border border-[#e8dcc8] bg-white"
                  data-testid="date-calendar"
                />
              </div>
              <div>
                <Label className="mb-2 block">Available times</Label>
                {selectedDate ? (
                  <div className="grid grid-cols-3 gap-2 max-h-96 overflow-y-auto" data-testid="time-slots-grid">
                    {generateTimeSlots().map((time) => {
                      const available = isSlotAvailable(time);
                      return (
                        <Button
                          key={time}
                          variant={selectedTime === time ? 'default' : 'outline'}
                          className={`${!available ? 'opacity-50 cursor-not-allowed' : ''} ${selectedTime === time ? 'bg-black text-[#e8dcc8]' : ''}`}
                          disabled={!available}
                          onClick={() => setSelectedTime(time)}
                          data-testid={`time-slot-${time.replace(':', '-')}`}
                        >
                          {time}
                        </Button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500">Select a date first</p>
                )}
              </div>
            </div>
            <Button 
              className="mt-6 w-full bg-black text-[#e8dcc8]" 
              disabled={!selectedDate || !selectedTime}
              onClick={() => setStep(4)}
              data-testid="next-to-client-data-btn"
            >
              Continue
            </Button>
          </div>
        )}

        {step === 4 && (
          <div data-testid="step-4-client-data">
            <h2 className="text-2xl font-bold mb-6">4. Your Details</h2>
            <Card>
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input 
                    id="name"
                    value={clientData.name}
                    onChange={(e) => setClientData({...clientData, name: e.target.value})}
                    data-testid="client-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input 
                    id="email"
                    type="email"
                    value={clientData.email}
                    onChange={(e) => setClientData({...clientData, email: e.target.value})}
                    data-testid="client-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input 
                    id="phone"
                    value={clientData.phone}
                    onChange={(e) => setClientData({...clientData, phone: e.target.value})}
                    placeholder="+61 4XX XXX XXX"
                    data-testid="client-phone-input"
                  />
                </div>
              </CardContent>
            </Card>
            <Button 
              className="mt-6 w-full bg-black text-[#e8dcc8]" 
              disabled={!clientData.name || !clientData.email || !clientData.phone}
              onClick={() => setStep(5)}
              data-testid="next-to-confirm-btn"
            >
              Review & Confirm
            </Button>
          </div>
        )}

        {step === 5 && (
          <div data-testid="step-5-confirm">
            <h2 className="text-2xl font-bold mb-6">5. Confirm & Pay</h2>
            <Card>
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label className="text-gray-600">Service</Label>
                  <p className="font-bold">{selectedService.name}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Duration</Label>
                  <p className="font-bold">{getSelectedDuration()} minutes</p>
                </div>
                <div>
                  <Label className="text-gray-600">Professional</Label>
                  <p className="font-bold">{selectedProfessional.name}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Date</Label>
                  <p className="font-bold">{selectedDate && format(selectedDate, "EEEE, d MMMM yyyy", { locale: enAU })}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Time</Label>
                  <p className="font-bold">{selectedTime}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Name</Label>
                  <p className="font-bold">{clientData.name}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Email</Label>
                  <p className="font-bold">{clientData.email}</p>
                </div>
                <div className="pt-4 border-t">
                  <Label className="text-gray-600">Total</Label>
                  <p className="text-2xl font-bold">{formatPrice(getSelectedPrice())}</p>
                </div>
              </CardContent>
            </Card>
            <Button 
              className="mt-6 w-full bg-black text-[#e8dcc8]" 
              onClick={handleConfirmBooking}
              data-testid="confirm-booking-btn"
            >
              <Check className="mr-2" /> Proceed to Payment
            </Button>
            <p className="text-sm text-gray-600 text-center mt-4">
              You will be redirected to secure payment. 50% cancellation fee applies for no-shows.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
